import React, { useState, useEffect, useRef } from 'react';
import { projectService } from '../services/projectService';
import {
  getStructuresByProjectId,
  getStructureWithRooms,
} from '../services/structureService';
import { getRoomType } from '../utils/floorPlanUtils';
import { roomDisplayName } from '@shared-lib/rooms/roomDisplayName';
import { homeownerShareService } from '../features/homeowner/services/homeownerShareService';
import { buildHomeownerHomeUrl } from '../features/homeowner/contracts';
import { describeInvitation } from './describeInvitation';
import {
  Modal,
  Button,
  Input,
  Select,
  Checkbox,
  IconButton,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from './ui';
import './ProjectSharingModal.css';

/** Absolute public URL for a homeowner share token. */
const absoluteHomeownerUrl = (token) =>
  `${window.location.origin}${buildHomeownerHomeUrl(token)}`;

/**
 * Label for a room row in the remodel-room checklist: its name the way a
 * person writes it ("livingRoom" → "Living room").
 */
const roomLabel = (room) =>
  roomDisplayName(room?.name) ?? (getRoomType(room) || 'Room');

/**
 * Copy-to-clipboard control: a read-only Input showing the public link with a
 * trailing IconButton that writes it to the clipboard. Mirrors the repo's
 * Input + trailing-icon pattern; falls back gracefully when the clipboard API
 * is unavailable.
 */
const CopyLinkField = ({ url }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <Input
      readOnly
      value={url}
      aria-label="Homeowner share link"
      onFocus={(e) => e.target.select()}
      trailingInteractive
      trailingIcon={
        <IconButton
          size="sm"
          variant="ghost"
          aria-label="Copy link"
          onClick={handleCopy}
        >
          {copied ? '✓' : '⧉'}
        </IconButton>
      }
    />
  );
};

/**
 * "Share with a homeowner" section: pick a structure, mark which rooms are
 * being remodeled, then create / copy / revoke a public `/h/:token` link.
 *
 * Owns its own data/loading/error state so the contractor section above stays
 * untouched. All backend access goes through `homeownerShareService`; those
 * wrappers may throw while the backend is implemented in parallel — every
 * async call is guarded.
 */
const HomeownerShareSection = ({ projectId }) => {
  const [structures, setStructures] = useState(null);
  const [structureId, setStructureId] = useState('');
  const [rooms, setRooms] = useState(null);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [remodelRoomIds, setRemodelRoomIds] = useState([]);
  const [shares, setShares] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  // Auto-save status for room toggles on an existing link:
  // 'idle' | 'saving' | 'saved' | 'error'.
  const [saveStatus, setSaveStatus] = useState('idle');

  // Debounce timer for auto-saving room toggles to the live link.
  const saveTimerRef = useRef(null);
  // True once the user toggles a room, so a background list refresh (after
  // create/revoke) doesn't clobber their in-progress selection.
  const userTouchedRoomsRef = useRef(false);
  // Ids revoked in this session. A revoke is terminal (a new link gets a new
  // doc id), so these are filtered out of every list refresh — otherwise a
  // stale read or a leftover duplicate-active doc could resurrect a link the
  // user just revoked.
  const revokedIdsRef = useRef(new Set());

  // Load the project's structures + existing homeowner links on mount.
  useEffect(() => {
    if (!projectId) return;
    // Fresh project = fresh revoke memory (also clears on remount/modal close).
    revokedIdsRef.current = new Set();
    loadStructures();
    loadShares();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Load rooms whenever the selected structure changes.
  useEffect(() => {
    if (!structureId) {
      setRooms(null);
      setRemodelRoomIds([]);
      return;
    }
    loadRooms(structureId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureId]);

  // A scan change re-seeds the checklist from that scan's link; clear the
  // "user edited" guard so the seed effect below runs for the new scan.
  // (Declared before the seed effect so it resets the ref first on change.)
  useEffect(() => {
    userTouchedRoomsRef.current = false;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setSaveStatus('idle');
  }, [structureId]);

  // Cancel any pending auto-save on unmount (e.g. modal close).
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // Seed the remodel-room checklist from the selected scan's existing link, so
  // re-opening pre-checks its rooms instead of starting empty. Stops once the
  // user edits the checklist, so a background list refresh (e.g. after an
  // auto-save) can't clobber their selection.
  useEffect(() => {
    if (!structureId || userTouchedRoomsRef.current) return;
    const existing = shares.find((s) => s.structureId === structureId);
    setRemodelRoomIds(existing?.remodelRoomIds || []);
  }, [structureId, shares]);

  const loadStructures = async () => {
    try {
      setError(null);
      const list = await getStructuresByProjectId(projectId);
      setStructures(list);
      // Auto-select + skip the picker when there's exactly one structure.
      if (list.length === 1) setStructureId(list[0].id);
    } catch (err) {
      console.error('Failed to load structures:', err);
      setError('Failed to load structures');
      setStructures([]);
    }
  };

  const loadRooms = async (id) => {
    try {
      setError(null);
      setRoomsLoading(true);
      const structure = await getStructureWithRooms(id);
      setRooms(structure?.rooms || []);
      // Checklist is seeded from the existing share by the seed effect above,
      // not reset here — resetting would wipe an existing link's rooms.
    } catch (err) {
      console.error('Failed to load rooms:', err);
      setError('Failed to load rooms for this structure');
      setRooms([]);
    } finally {
      setRoomsLoading(false);
    }
  };

  const loadShares = async () => {
    try {
      const list = await homeownerShareService.listShares(projectId);
      const arr = Array.isArray(list) ? list : [];
      // Defensively drop revoked links (enabled:false) in case any backend/
      // emulator version returns them, plus anything revoked this session so a
      // stale/duplicate refetch can't resurrect it. The single place server
      // shares are applied to state.
      setShares(
        arr.filter(
          (s) => s.enabled !== false && !revokedIdsRef.current.has(s.id)
        )
      );
    } catch (err) {
      // Backend may be unimplemented; treat as "no links yet".
      console.error('Failed to list homeowner shares:', err);
      setShares([]);
    }
  };

  const toggleRoom = (roomId) => {
    userTouchedRoomsRef.current = true;
    const next = remodelRoomIds.includes(roomId)
      ? remodelRoomIds.filter((id) => id !== roomId)
      : [...remodelRoomIds, roomId];
    setRemodelRoomIds(next);
    // With a live link, persist the change automatically (debounced). Without
    // one, the explicit "Create link" button is the save action.
    if (existingShare) scheduleAutoSave(structureId, next);
  };

  // Debounce room toggles into a single backend write to the live link.
  const scheduleAutoSave = (scanId, roomIds) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      autoSaveRooms(scanId, roomIds);
    }, 500);
  };

  // Write the updated remodel-room set to the scan's existing link (createShare
  // reuses the one link and rewrites its rooms), then sync local state so the
  // link card stays consistent without a full refetch.
  const autoSaveRooms = async (scanId, roomIds) => {
    try {
      setSaveStatus('saving');
      await homeownerShareService.createShare(projectId, scanId, roomIds);
      setShares((prev) =>
        prev.map((s) =>
          s.structureId === scanId && s.enabled !== false
            ? { ...s, remodelRoomIds: roomIds }
            : s
        )
      );
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (err) {
      console.error('Failed to update homeowner link:', err);
      setSaveStatus('error');
    }
  };

  const handleCreate = async () => {
    if (!structureId) return;
    try {
      setError(null);
      setCreating(true);
      await homeownerShareService.createShare(
        projectId,
        structureId,
        remodelRoomIds
      );
      await loadShares();
    } catch (err) {
      console.error('Failed to create homeowner link:', err);
      setError(err.message || 'Failed to create homeowner link');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (shareId) => {
    if (!window.confirm('Revoke this homeowner link? It will stop working.'))
      return;
    try {
      setError(null);
      await homeownerShareService.revokeShare(shareId);
      // Remember + optimistically drop it now so the UI updates instantly. The
      // id stays excluded so the refetch below (which can briefly still return
      // the just-revoked doc due to read staleness) can't bring it back.
      revokedIdsRef.current.add(shareId);
      // Revoke is terminal: cancel any pending room auto-save and let the
      // checklist reseed (now to empty) for a fresh "create link" state.
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      setSaveStatus('idle');
      userTouchedRoomsRef.current = false;
      setShares((prev) => prev.filter((s) => s.id !== shareId));
      await loadShares();
    } catch (err) {
      console.error('Failed to revoke homeowner link:', err);
      setError(err.message || 'Failed to revoke homeowner link');
    }
  };

  const showPicker = (structures?.length || 0) > 1;
  const selectedStructure = structures?.find((s) => s.id === structureId);
  // One link per scan: the scan's existing link (if any) drives the whole tab.
  const existingShare = shares.find((s) => s.structureId === structureId);
  const scanName = selectedStructure?.name || 'this scan';

  return (
    <div className="homeowner-section">
      <h4>Share with a Homeowner</h4>
      <p className="homeowner-description">
        Create a public, view-only link a homeowner can open without an account.
        Pick a scan and mark the rooms being remodeled — those become the
        homeowner's room switcher.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {structures === null ? (
        <p className="homeowner-loading">Loading scans…</p>
      ) : structures.length === 0 ? (
        <p className="no-viewers">No scans available to share</p>
      ) : (
        <>
          {showPicker ? (
            <div className="homeowner-field">
              <Select
                label="Scan to share"
                value={structureId}
                onChange={(e) => setStructureId(e.target.value)}
                options={[
                  { value: '', label: 'Select a scan…' },
                  ...structures.map((s, i) => ({
                    value: s.id,
                    label: s.name || `Scan ${i + 1}`,
                  })),
                ]}
              />
            </div>
          ) : (
            selectedStructure && (
              <p className="homeowner-single-scan">
                Scan: <strong>{selectedStructure.name || 'Scan 1'}</strong>
              </p>
            )
          )}

          {/* Link card on top once the scan has a link. */}
          {structureId && existingShare && (
            <div className="homeowner-field">
              <h5 className="homeowner-subheading">
                Homeowner link for {scanName}
              </h5>
              <div className="homeowner-link-card">
                <div className="homeowner-link-copy">
                  <CopyLinkField
                    url={absoluteHomeownerUrl(existingShare.token)}
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleRevoke(existingShare.id)}
                >
                  Revoke
                </Button>
              </div>
            </div>
          )}

          {/* Room checklist. With a link, toggles auto-save to it. */}
          {structureId && (
            <div className="homeowner-field">
              <div className="homeowner-subheading-row">
                <h5 className="homeowner-subheading">
                  {existingShare
                    ? 'Rooms shared in this link'
                    : 'Rooms being remodeled'}
                </h5>
                {existingShare && saveStatus !== 'idle' && (
                  <span
                    className={`homeowner-save-status homeowner-save-status--${saveStatus}`}
                  >
                    {saveStatus === 'saving' && 'Saving…'}
                    {saveStatus === 'saved' && 'Saved ✓'}
                    {saveStatus === 'error' && "Couldn't save — try again"}
                  </span>
                )}
              </div>
              {roomsLoading ? (
                <p className="homeowner-loading">Loading rooms…</p>
              ) : (rooms?.length || 0) === 0 ? (
                <p className="no-viewers">No rooms in this scan</p>
              ) : (
                <div className="homeowner-room-list">
                  {rooms.map((room) => (
                    <Checkbox
                      key={room.id}
                      checked={remodelRoomIds.includes(room.id)}
                      onChange={() => toggleRoom(room.id)}
                    >
                      {roomLabel(room)}
                    </Checkbox>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* No link yet → explicit create. With a link, edits auto-save. */}
          {structureId && !existingShare && (
            <Button
              variant="primary"
              onClick={handleCreate}
              loading={creating}
              disabled={creating || roomsLoading}
            >
              Create link
            </Button>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Backwards-compatible ProjectSharingModal wrapper around the Modal primitive.
 *
 * Public props (unchanged):
 *   project, isOpen, onClose
 *
 * Body keeps the existing `sharing-*`, `viewers-*`, `user-card` etc. classes
 * so the rich list of viewers/invitations continues to render identically.
 */
const ProjectSharingModal = ({ project, isOpen, onClose }) => {
  const [collaborators, setCollaborators] = useState(null);
  const [newViewerEmail, setNewViewerEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showInviteOption, setShowInviteOption] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('collaborator');

  useEffect(() => {
    if (isOpen && project) {
      loadCollaborators();
    }
  }, [isOpen, project]);

  const loadCollaborators = async () => {
    try {
      setError(null);
      const data = await projectService.getProjectCollaborators(project.id);
      setCollaborators(data);
    } catch (error) {
      console.error('Failed to load collaborators:', error);
      setError('Failed to load collaborators');
    }
  };

  const handleAddViewer = async () => {
    if (!newViewerEmail) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    setShowInviteOption(false);

    try {
      const result = await projectService.addProjectViewerByEmail(
        project.id,
        newViewerEmail
      );

      if (result.success) {
        await loadCollaborators();
        setNewViewerEmail('');
        const who = result.user.displayName || result.user.email;
        // Tri-state on purpose: hosting and functions deploy independently, so
        // `notified` is ABSENT against a backend older than #2202. Undefined
        // claims nothing either way rather than promising an email that was
        // never sent or reporting a failure that never happened.
        setSuccess(
          result.notified === true
            ? `Added ${who} as viewer. We emailed them a link to the project.`
            : result.notified === false
              ? `Added ${who} as viewer, but we could not email them about it.`
              : `Added ${who} as viewer`
        );
      } else if (result.canInvite) {
        setShowInviteOption(true);
        setError(
          'No Housfully user found with this email. You can send them an invitation to join.'
        );
      } else {
        setError('Failed to add viewer');
      }
    } catch (error) {
      // The callable's own messages are the accurate ones: it distinguishes
      // "already has access" from "already owns the project", and a sniff for
      // 'already' collapsed both onto the first (#2202) — while never firing
      // for the re-add case at all, which used to succeed silently.
      console.error('Error adding viewer:', error);
      setError(error.message || 'Failed to add viewer');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!newViewerEmail) return;

    setInviteLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await projectService.sendProjectInvitation(
        project.id,
        newViewerEmail
      );
      // Refresh so the new invitation appears under Outstanding Invitations.
      // Every other handler here reloads — including RESEND, which calls this
      // same callable — so sending the first one was the only action whose
      // result the list did not show until the modal was reopened.
      await loadCollaborators();
      setNewViewerEmail('');
      setShowInviteOption(false);
      const message = result.isResend
        ? `Invitation resent to ${newViewerEmail}. They will receive a new email with instructions to join Housfully.`
        : `Invitation sent to ${newViewerEmail}. They will receive an email with instructions to join Housfully and access this project.`;
      setSuccess(message);
    } catch (error) {
      console.error('Error sending invitation:', error);
      if (
        error.message?.includes('already-exists') &&
        error.message?.includes('User already exists')
      ) {
        setError(
          'This user already exists. Please use the regular sharing option.'
        );
      } else if (error.message?.includes('Use regular sharing')) {
        setError(
          'This user already exists. Please use the regular sharing option.'
        );
      } else {
        setError(error.message || 'Failed to send invitation');
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveViewer = async (viewerId, viewerName) => {
    if (!window.confirm(`Remove ${viewerName} from this project?`)) return;

    try {
      setError(null);
      await projectService.removeProjectViewer(project.id, viewerId);
      await loadCollaborators();
      setSuccess('Viewer removed');
    } catch (error) {
      console.error('Error removing viewer:', error);
      setError(error.message || 'Failed to remove viewer');
    }
  };

  const handleCancelInvitation = async (invitationId, email) => {
    if (!window.confirm(`Cancel invitation to ${email}?`)) return;

    try {
      setError(null);
      await projectService.cancelProjectInvitation(invitationId);
      await loadCollaborators();
      setSuccess('Invitation cancelled');
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      setError(error.message || 'Failed to cancel invitation');
    }
  };

  const handleResendInvitation = async (email) => {
    try {
      setError(null);
      setLoading(true);
      await projectService.sendProjectInvitation(project.id, email);
      await loadCollaborators();
      setSuccess(`Invitation resent to ${email}`);
    } catch (error) {
      console.error('Error resending invitation:', error);
      setError(error.message || 'Failed to resend invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} title="Share Project" size="md">
      <div className="project-info">
        <h3>{project?.name}</h3>
      </div>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <TabList>
          <Tab value="collaborator">Collaborator</Tab>
          <Tab value="homeowner">Homeowner</Tab>
        </TabList>

        <TabPanel value="collaborator">
          <p className="share-description">
            Share this project with other Housfully users for view-only access.
            Viewers can browse your project and create their own copy if they
            want to make changes.
          </p>

          {error && <div className="alert alert-error">{error}</div>}

          {success && <div className="alert alert-success">{success}</div>}

          <div className="collaborators-section">
            <div className="add-viewer-section">
              <h4>Add Viewer</h4>
              <div className="add-viewer-form">
                <div className="email-form-row">
                  {loading || inviteLoading ? (
                    <div className="adding-message">
                      {loading ? 'Adding viewer...' : 'Sending invitation...'}
                    </div>
                  ) : (
                    <>
                      <div style={{ flex: 1 }}>
                        <Input
                          type="email"
                          placeholder="Enter user's email"
                          value={newViewerEmail}
                          onChange={(e) => setNewViewerEmail(e.target.value)}
                          onKeyPress={(e) =>
                            e.key === 'Enter' && handleAddViewer()
                          }
                          disabled={loading || inviteLoading}
                        />
                      </div>
                      <Button
                        variant="primary"
                        onClick={handleAddViewer}
                        disabled={loading || inviteLoading || !newViewerEmail}
                      >
                        Add
                      </Button>
                    </>
                  )}
                </div>

                {showInviteOption && newViewerEmail && (
                  <div className="invite-option">
                    <p className="invite-suggestion">
                      Want to invite <strong>{newViewerEmail}</strong> to join
                      Housfully?
                    </p>
                    <Button
                      variant="primary"
                      onClick={handleSendInvitation}
                      disabled={inviteLoading}
                      loading={inviteLoading}
                    >
                      Send Invitation Email
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="owner-section">
              <h4>Owner</h4>
              {collaborators?.owner && (
                <div className="user-card owner-card">
                  <div className="user-info">
                    <div>
                      {collaborators.owner.displayName && (
                        <div className="user-name">
                          {collaborators.owner.displayName}
                        </div>
                      )}
                      <div className="user-email">
                        {collaborators.owner.email}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="viewers-section">
              <h4>Viewers ({collaborators?.viewers?.length || 0})</h4>
              {collaborators?.viewers?.length > 0 ? (
                <div className="viewers-list">
                  {collaborators.viewers.map((viewer) => (
                    <div key={viewer.uid} className="user-card viewer-card">
                      <div className="user-info">
                        <div>
                          {viewer.displayName && (
                            <div className="user-name">
                              {viewer.displayName}
                            </div>
                          )}
                          <div className="user-email">{viewer.email}</div>
                        </div>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          handleRemoveViewer(
                            viewer.uid,
                            viewer.displayName || viewer.email
                          )
                        }
                        title="Remove viewer"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-viewers">No viewers yet</p>
              )}
            </div>

            <div className="pending-invitations-section">
              <h4>
                Outstanding Invitations (
                {collaborators?.pendingInvitations?.length || 0})
              </h4>
              {collaborators?.pendingInvitations?.length > 0 ? (
                <div className="viewers-list">
                  {collaborators.pendingInvitations.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="user-card invitation-card"
                    >
                      <div className="user-info">
                        <div>
                          <div className="user-email">{invitation.email}</div>
                          <div className="invitation-meta">
                            {describeInvitation(invitation)}
                          </div>
                        </div>
                      </div>
                      <div className="invitation-actions">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            handleResendInvitation(invitation.email)
                          }
                          title="Resend invitation email"
                        >
                          Resend
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            handleCancelInvitation(
                              invitation.id,
                              invitation.email
                            )
                          }
                          title="Cancel invitation"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-viewers">No outstanding invitations</p>
              )}
            </div>
          </div>
        </TabPanel>

        <TabPanel value="homeowner">
          <HomeownerShareSection projectId={project?.id} />
        </TabPanel>
      </Tabs>
    </Modal>
  );
};

export default ProjectSharingModal;
